import React from 'react';
import Icon from '../../components/AppIcon';
import StatusIndicatorBadge from '../../components/ui/StatusIndicatorBadge';
import PaginationComponent from '../../components/PaginationComponent';
import { statusToText, workerStatusHelpers, statusHelpers } from '../../constants/statusUtils';

const getUserDisplayName = (user) => {
  if (!user) return '';
  return user.full_name || user.fullName || user.username || '';
};

// Transform API pagination format to component format
const transformPagination = (apiPagination) => {
  if (!apiPagination) {
    return {
      currentPage: 1,
      perPage: 10,
      total: 0,
      totalPages: 1,
      from: 0,
      to: 0
    };
  }

  return {
    currentPage: apiPagination.current_page || apiPagination.page || 1,
    perPage: apiPagination.per_page || apiPagination.limit || 10,
    total: apiPagination.total || 0,
    totalPages: apiPagination.total_pages || apiPagination.totalPages || 1,
    from: apiPagination.from || 0,
    to: apiPagination.to || 0
  };
};

export default function SelectionRequestTable({
  applications,
  onApprove,
  onReject,
  onViewDetails,
  processing,
  activeTab,
  pagination,
  onPageChange,
  onItemsPerPageChange
}) {
  const buildQuotaProgress = (application, workerTotals) => {
    const qp = application.quota_progress || {};
    const total = qp.total ?? application.quota ?? application.fcl_count ?? workerTotals.total ?? 0;
    const submitted = qp.submitted ?? workerTotals.total ?? 0;
    const approved = qp.approved ?? workerTotals.approved ?? 0;
    const rejected = qp.rejected ?? workerTotals.rejected ?? 0;
    const pending = qp.pending ?? workerTotals.pending ?? 0;
    const remaining = qp.remaining ?? Math.max(total - submitted, 0);

    return { total, submitted, approved, rejected, pending, remaining };
  };
  
  // Helper function to get the appropriate date and label based on status
  const getDateInfo = (application) => {
    const status = application.status;
    
    if (statusHelpers.isApproved(status)) {
      return {
        date: application.approved_at || application.updated_at,
        label: 'Approved Date',
        icon: 'CheckCircle',
        color: 'text-green-600'
      };
    } else if (statusHelpers.isRejected(status)) {
      return {
        date: application.rejected_at || application.updated_at,
        label: 'Rejected Date',
        icon: 'XCircle',
        color: 'text-red-600'
      };
    } else if (statusHelpers.isResubmit(status)) {
      return {
        date: application.resubmitted_at || application.updated_at,
        label: 'Resubmitted Date',
        icon: 'RefreshCw',
        color: 'text-blue-600'
      };
    } else {
      // Pending or other statuses
      return {
        date: application.created_at,
        label: 'Submission Date',
        icon: 'Calendar',
        color: 'text-gray-600'
      };
    }
  };

  const paginationData = transformPagination(pagination);

  // Empty state
  if (!applications || applications.length === 0) {
    return (
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="text-center py-12">
          <Icon name="Inbox" size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-sm font-medium text-gray-900 mb-1">No applications found</h3>
          <p className="text-sm text-gray-500">There are no selection requests to display.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Application / Quota</th>
              <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Quota Progress</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VDR Type</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company Name</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total Workers</th>
              <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Rejected</th>
              <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Approved</th>
              <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Pending</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action Date</th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reviewed By</th>
              <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {applications.map((application) => {
              const totalBiodata = application.workers?.length || 0;
              const totalReject = application.workers?.filter(w => workerStatusHelpers.isRejected(w.status)).length || 0;
              const totalApproved = application.workers?.filter(w => workerStatusHelpers.isApproved(w.status)).length || 0;
              const totalPending = application.workers?.filter(w => workerStatusHelpers.isPending(w.status)).length || 0;
              const allRejected = application.workers?.length > 0 && application.workers.every(w => workerStatusHelpers.isRejected(w.status));
              const quotaProgress = buildQuotaProgress(application, {
                total: totalBiodata,
                approved: totalApproved,
                rejected: totalReject,
                pending: totalPending
              });
              
              const dateInfo = getDateInfo(application);
              
              return (
                <tr key={application.id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <Icon name="FileText" size={16} className="text-blue-500" />
                        <span className="font-mono text-sm font-medium text-gray-900">
                          {application.reference_number || `APP-${application.id}`}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Quota Ref:{" "}
                        <span className="font-semibold text-gray-800">
                          {application.quota_reference_no || application.vdr_id || "—"}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="space-y-1 text-xs text-gray-700">
                      <div className="flex items-center justify-center gap-2">
                        <span className="font-semibold text-gray-900">{quotaProgress.submitted}</span>
                        <span className="text-gray-500">/</span>
                        <span className="font-semibold text-gray-900">{quotaProgress.total}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500"
                          style={{ width: `${Math.min(100, (quotaProgress.submitted / (quotaProgress.total || 1)) * 100)}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-center gap-2 text-gray-500">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-50 text-green-700">
                          Rem: {quotaProgress.remaining}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                      <Icon name="Briefcase" size={12} className="mr-1" />
                      {application.vdr_type || 'SPECIAL QUOTA'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="Building" size={16} className="text-green-500" />
                      <span className="text-sm font-medium text-gray-900">
                        {application.employer?.company_name || 'N/A'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <StatusIndicatorBadge status={statusToText(application.status).toLowerCase()} size="sm" />
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-lg font-semibold text-gray-900">{totalBiodata}</span>
                    <div className="text-xs text-gray-500">Batch size</div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`text-lg font-semibold ${totalReject > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {totalReject}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`text-lg font-semibold ${totalApproved > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                      {totalApproved}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`text-lg font-semibold ${totalPending > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>
                      {totalPending}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-1">
                      <div className="flex items-center space-x-2 text-sm">
                        <Icon name={dateInfo.icon} size={14} className={dateInfo.color} />
                        <span className="font-medium text-gray-900">
                          {dateInfo.date ? new Date(dateInfo.date).toLocaleDateString('en-MY', {
                            year: 'numeric', month: 'short', day: 'numeric',
                            timeZone: 'Asia/Kuala_Lumpur'
                          }) : '-'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {dateInfo.label}
                      </div>
                      {dateInfo.date && (
                        <div className="text-xs text-gray-400">
                          {new Date(dateInfo.date).toLocaleTimeString('en-MY', {
                            hour: '2-digit', minute: '2-digit',
                            timeZone: 'Asia/Kuala_Lumpur'
                          })}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-1">
                      {application.reviewedByUser && (
                        <div className="flex items-center space-x-2 text-sm">
                          <Icon name="User" size={14} className="text-blue-500" />
                          <span className="font-medium text-gray-900">
                            {getUserDisplayName(application.reviewedByUser)}
                          </span>
                        </div>
                      )}
                      {application.reviewed_at && (
                        <div className="text-xs text-gray-500">
                          {new Date(application.reviewed_at).toLocaleDateString('en-MY', {
                            year: 'numeric', month: 'short', day: 'numeric',
                            timeZone: 'Asia/Kuala_Lumpur'
                          })}
                        </div>
                      )}
                      {!application.reviewedByUser && (
                        <span className="text-xs text-gray-400">Not reviewed</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center space-x-2">
                      <button
                        onClick={() => onViewDetails(application)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150 group"
                        title="View Details"
                      >
                        <Icon name="Eye" size={18} className="group-hover:scale-110 transition-transform duration-150" />
                      </button>
                      {activeTab === 'pending' && (
                        <>
                          <button
                            onClick={() => onApprove(application)}
                            disabled={processing || allRejected}
                            className={`p-2 rounded-lg transition-all duration-150 group ${
                              allRejected 
                                ? 'text-gray-400 cursor-not-allowed' 
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                            title={allRejected ? 'Cannot approve: all workers have been rejected' : 'Approve'}
                          >
                            <Icon name="CheckCircle" size={18} className={!allRejected ? 'group-hover:scale-110 transition-transform duration-150' : ''} />
                          </button>
                          <button
                            onClick={() => onReject(application)}
                            disabled={processing}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150 disabled:opacity-50 group"
                            title="Reject"
                          >
                            <Icon name="XCircle" size={18} className="group-hover:scale-110 transition-transform duration-150" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile/Tablet Cards */}
      <div className="lg:hidden divide-y divide-gray-200">
        {applications.map((application) => {
          const totalBiodata = application.workers?.length || 0;
          const totalReject = application.workers?.filter(w => workerStatusHelpers.isRejected(w.status)).length || 0;
          const totalApproved = application.workers?.filter(w => workerStatusHelpers.isApproved(w.status)).length || 0;
          const totalPending = application.workers?.filter(w => workerStatusHelpers.isPending(w.status)).length || 0;
          const allRejected = application.workers?.length > 0 && application.workers.every(w => workerStatusHelpers.isRejected(w.status));
          const quotaProgress = buildQuotaProgress(application, {
            total: totalBiodata,
            approved: totalApproved,
            rejected: totalReject,
            pending: totalPending
          });
          
          const dateInfo = getDateInfo(application);
          
          return (
            <div key={application.id} className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <Icon name="FileText" size={20} className="text-blue-500 flex-shrink-0" />
                  <div>
                    <h4 className="font-mono text-sm font-medium text-gray-900">
                      {application.reference_number || `APP-${application.id}`}
                    </h4>
                    <p className="text-sm text-gray-600 flex items-center space-x-1">
                      <Icon name="Building" size={14} className="text-green-500" />
                      <span>{application.employer?.company_name || 'N/A'}</span>
                    </p>
                  </div>
                </div>
                <StatusIndicatorBadge status={statusToText(application.status).toLowerCase()} size="sm" />
              </div>
              
              <div className="mt-4 space-y-3 text-sm text-gray-700">
                <div className="flex justify-between">
                  <strong>Quota:</strong>
                  <div className="text-right">
                    <div className="flex items-center justify-end gap-1 text-sm font-semibold text-gray-900">
                      <span>{quotaProgress.submitted}</span>
                      <span className="text-gray-500">/</span>
                      <span>{quotaProgress.total}</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mt-1">
                      <div
                        className="h-full bg-blue-500"
                        style={{ width: `${Math.min(100, (quotaProgress.submitted / (quotaProgress.total || 1)) * 100)}%` }}
                      />
                    </div>
                    <div className="text-[11px] text-green-700 mt-1">Remaining: {quotaProgress.remaining}</div>
                  </div>
                </div>
                {application.vdr_type && (
                  <div className="flex justify-between">
                    <strong>VDR Type:</strong>
                    <span className="inline-flex items-center px-2.5 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                      <Icon name="Briefcase" size={12} className="mr-1" />
                      {application.vdr_type}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <strong>Total Workers:</strong>
                  <span className="text-lg font-semibold text-gray-900">{totalBiodata}</span>
                </div>
                <div className="flex justify-between">
                  <strong>Workers Status:</strong>
                  <div className="text-xs space-x-2">
                    <span className={`font-semibold ${totalApproved > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                      A: {totalApproved}
                    </span>
                    <span className={`font-semibold ${totalReject > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      R: {totalReject}
                    </span>
                    <span className={`font-semibold ${totalPending > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>
                      P: {totalPending}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between">
                  <strong>{dateInfo.label}:</strong>
                  <div className="text-right">
                    <div className="flex items-center space-x-1 text-sm">
                      <Icon name={dateInfo.icon} size={12} className={dateInfo.color} />
                      <span className="font-medium">
                        {dateInfo.date ? new Date(dateInfo.date).toLocaleDateString('en-MY', {
                          year: 'numeric', month: 'short', day: 'numeric',
                          timeZone: 'Asia/Kuala_Lumpur'
                        }) : '-'}
                      </span>
                    </div>
                    {dateInfo.date && (
                      <div className="text-xs text-gray-400">
                        {new Date(dateInfo.date).toLocaleTimeString('en-MY', {
                          hour: '2-digit', minute: '2-digit',
                          timeZone: 'Asia/Kuala_Lumpur'
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-gray-200 flex justify-end items-center space-x-2">
                <button
                  onClick={() => onViewDetails(application)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                  title="View Details"
                >
                  <Icon name="Eye" size={18} />
                </button>
                {activeTab === 'pending' && (
                  <>
                    <button
                      onClick={() => onApprove(application)}
                      disabled={processing || allRejected}
                      className={`p-2 rounded-lg transition-colors duration-150 ${
                        allRejected 
                          ? 'text-gray-400 cursor-not-allowed' 
                          : 'text-green-600 hover:bg-green-50'
                      }`}
                      title={allRejected ? 'Cannot approve: all workers have been rejected' : 'Approve'}
                    >
                      <Icon name="CheckCircle" size={18} />
                    </button>
                    <button
                      onClick={() => onReject(application)}
                      disabled={processing}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150 disabled:opacity-50"
                      title="Reject"
                    >
                      <Icon name="XCircle" size={18} />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination Controls */}
      {paginationData.total > 0 && (
        <div className="px-4 py-3 border-t border-gray-200">
          <PaginationComponent
            pagination={paginationData}
            onPageChange={onPageChange}
            onPerPageChange={onItemsPerPageChange}
            showPerPageSelector={true}
            maxVisiblePages={5}
          />
        </div>
      )}
    </div>
  );
}
